import React, { useState } from 'react'
import * as api from '../../../lib/api'
import { useToast } from '../../../contexts/ToastContext'
import { PERMISSION_DEFAULTS } from '../../../lib/permissionDefaults'
import { useAuth } from '../../../contexts/AuthContext'

const ROLES = ['employee', 'helper', 'admin']
const ROLE_LABELS = { employee: 'Сотрудник', helper: 'Помощник', admin: 'Админ' }

// Структура разделов и их прав
const SECTIONS = [
  {
    id: 'dashboard',
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
    title: 'Дашборд',
    desc: 'Главная страница со сводкой по ПВЗ',
    accessKey: 'canAccessDashboard',
    note: 'Финансовые данные контролируются отдельно правом «Видеть доходы» в разделе Аналитика',
    permissions: []
  },
  {
    id: 'operations',
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>,
    title: 'Операции',
    desc: 'Приёмка, выдача, возвраты товаров',
    accessKey: 'canAccessOperations',
    permissions: [
      { key: 'canManageOperations', label: 'Редактировать чужие', desc: 'Может изменять и удалять записи других сотрудников' },
      { key: 'canAddOperationsForOthers', label: 'Добавлять за других', desc: 'Может вносить операции от лица коллег' },
    ]
  },
  {
    id: 'payments',
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
    title: 'Зарплаты',
    desc: 'Начисления, штрафы, выплаты',
    accessKey: 'canAccessPayments',
    permissions: [
      { key: 'canManagePayments', label: 'Премии и штрафы', desc: 'Добавление и удаление ручных начислений' },
      { key: 'canManageDeductions', label: 'Удержания WB', desc: 'Распределение штрафов WB между сотрудниками' },
    ]
  },
  {
    id: 'schedule',
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
    title: 'График работы',
    desc: 'Расписание смен сотрудников',
    accessKey: 'canAccessSchedule',
    permissions: [
      { key: 'canEditSchedule', label: 'Редактировать смены', desc: 'Может изменять и утверждать график работы других' },
    ]
  },
  {
    id: 'chat',
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
    title: 'Чаты',
    desc: 'Общение команды ПВЗ',
    accessKey: 'canAccessChats',
    permissions: []
  },
  {
    id: 'tasks',
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/></svg>,
    title: 'Задачи',
    desc: 'Планировщик задач и поручений',
    accessKey: 'canAccessTasks',
    permissions: []
  },
  {
    id: 'vacancies',
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
    title: 'Вакансии',
    desc: 'Управление вакансиями и анкетами',
    accessKey: 'canAccessVacancies',
    permissions: [
      { key: 'canViewApplications', label: 'Видеть отклики', desc: 'Просмотр анкет кандидатов' },
      { key: 'canManageVacancies', label: 'Управлять вакансиями', desc: 'Создание и редактирование вакансий' },
    ]
  },
  {
    id: 'cameras',
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>,
    title: 'Камеры',
    desc: 'Видеонаблюдение на ПВЗ',
    accessKey: 'canAccessCameras',
    permissions: []
  },
  {
    id: 'ai_assistant',
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
    title: 'ИИ Ассистент',
    desc: 'Умный помощник с аналитикой',
    accessKey: 'canAccessAI',
    note: 'Уровень информации ИИ адаптируется автоматически под роль пользователя',
    permissions: []
  },
  {
    id: 'analytics',
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
    title: 'Аналитика',
    desc: 'Доходы, расходы, P&L',
    accessKey: 'canAccessAnalytics',
    permissions: [
      { key: 'canSeeIncome', label: 'Видеть доходы и прибыль', desc: 'Доступ к финансовой информации ПВЗ (в т.ч. на Дашборде)' },
      { key: 'canManageExpenses', label: 'Управлять расходами', desc: 'Добавление и редактирование расходов ПВЗ' },
    ]
  },
  {
    id: 'settings',
    icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
    title: 'Настройки',
    desc: 'Профиль, аккаунт и настройки приложения',
    accessKey: 'canAccessSettings',
    note: 'Сотрудники видят только свой профиль и аккаунт. Изменение общих настроек админом требует одобрения.',
    permissions: [
      { key: 'canManageTeam', label: 'Управление командой', desc: 'Просмотр списка сотрудников' },
      { key: 'canManageSalaryConfig', label: 'Настройки ЗП', desc: 'Управление правилами расчета зарплат' },
      { key: 'canManageGeneralSettings', label: 'Общие настройки', desc: 'Прямое изменение общих настроек ПВЗ' },
    ]
  },
]

function getVal(perms, key, role) {
  if (perms && key in perms) return perms[key] === true
  return PERMISSION_DEFAULTS[role]?.[key] ?? true
}

// Компонент тумблера
function Toggle({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      style={{
        width: '42px',
        height: '24px',
        borderRadius: '12px',
        border: 'none',
        padding: 0,
        cursor: disabled ? 'not-allowed' : 'pointer',
        backgroundColor: checked ? 'var(--wb-primary)' : 'var(--surface-border)',
        position: 'relative',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        opacity: disabled ? 0.35 : 1,
        flexShrink: 0,
        boxShadow: checked ? '0 2px 8px rgba(139, 92, 246, 0.25)' : 'none',
      }}
    >
      <span style={{
        position: 'absolute',
        top: '3px',
        left: checked ? '21px' : '3px',
        width: '18px',
        height: '18px',
        borderRadius: '50%',
        backgroundColor: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }} />
    </button>
  )
}

export default function PermissionsSettings({
  localRolePermissions,
  setLocalRolePermissions,
  profile
}) {
  const toast = useToast()
  const { fetchProfile } = useAuth()
  const [expandedSections, setExpandedSections] = useState([])
  const [hoveredSection, setHoveredSection] = useState(null)

  const toggleExpand = (sectionId) => {
    setExpandedSections(prev =>
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    )
  }

  const handlePermChange = async (role, key, checked) => {
    const newPerms = { ...localRolePermissions[role], [key]: checked }
    setLocalRolePermissions({ ...localRolePermissions, [role]: newPerms })
    try {
      await api.updateOrgRolePermissions(profile.org_id, role, newPerms)
      await fetchProfile()
      toast.success('Права обновлены')
    } catch (err) {
      toast.error('Ошибка: ' + err.message)
      setLocalRolePermissions(prev => ({
        ...prev,
        [role]: { ...prev[role], [key]: !checked }
      }))
    }
  }

  return (
    <div className="card" style={{ padding: '28px', marginTop: 20, background: 'var(--surface)', border: '1px solid var(--surface-border)', boxShadow: 'var(--surface-shadow)' }}>
      <div style={{ marginBottom: 28 }}>
        <h3 className="settings-section-title" style={{ marginBottom: 8, fontSize: '1.25rem', fontWeight: 700 }}>
          Матрица прав доступа
        </h3>
        <p className="salary-config-desc" style={{ color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5, fontSize: '13.5px' }}>
          Управляйте видимостью разделов и действиями для каждой должности.
          Владелец всегда имеет полный доступ ко всему.
        </p>
      </div>

      {/* Заголовок колонок ролей */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr repeat(3, 90px)',
        gap: '8px',
        padding: '0 20px 12px',
        borderBottom: '2px solid var(--surface-border)',
        marginBottom: '16px',
      }}>
        <div style={{ fontWeight: 700, fontSize: '12.5px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
          Раздел
        </div>
        {ROLES.map(role => (
          <div key={role} style={{ textAlign: 'center', fontWeight: 700, fontSize: '12px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {ROLE_LABELS[role]}
          </div>
        ))}
      </div>

      {/* Список разделов */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {SECTIONS.map(section => {
          const isExpanded = expandedSections.includes(section.id)
          const hasPerms = section.permissions.length > 0 || section.note

          return (
            <div
              key={section.id}
              onMouseEnter={() => setHoveredSection(section.id)}
              onMouseLeave={() => setHoveredSection(null)}
              style={{
                borderRadius: '12px',
                border: isExpanded 
                  ? '1px solid var(--wb-primary)' 
                  : (hoveredSection === section.id ? '1px solid var(--text-tertiary)' : '1px solid var(--surface-border)'),
                backgroundColor: isExpanded ? 'var(--bg-secondary)' : 'var(--surface)',
                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                transform: (hoveredSection === section.id && !isExpanded) ? 'translateY(-1px)' : 'translateY(0)',
                boxShadow: isExpanded || hoveredSection === section.id 
                  ? '0 6px 20px rgba(99, 102, 241, 0.04)' 
                  : 'none',
              }}
            >
              {/* Строка раздела */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr repeat(3, 90px)',
                gap: '8px',
                padding: '14px 20px',
                alignItems: 'center',
              }}>
                {/* Название + иконка + expand */}
                <div
                  onClick={() => hasPerms && toggleExpand(section.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    cursor: hasPerms ? 'pointer' : 'default',
                    userSelect: 'none',
                    flex: 1,
                    minWidth: 0
                  }}
                >
                  <div style={{
                    width: '38px',
                    height: '38px',
                    borderRadius: '10px',
                    background: isExpanded ? 'var(--wb-primary-ultra-light)' : 'var(--bg-secondary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '18px',
                    flexShrink: 0,
                    transition: 'all 0.25s ease',
                    color: isExpanded ? 'var(--wb-primary)' : 'inherit',
                  }}>
                    {section.icon}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: '15px', color: 'var(--text-primary)' }}>
                      {section.title}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '3.5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {section.desc}
                    </div>
                  </div>
                  {hasPerms && (
                    <svg
                      width="16" height="16" viewBox="0 0 24 24"
                      fill="none" stroke="var(--text-tertiary)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                      style={{
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.25s ease',
                        flexShrink: 0,
                        marginRight: '8px'
                      }}
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  )}
                </div>

                {/* Тумблеры для каждой роли */}
                {ROLES.map(role => (
                  <div key={role} style={{ display: 'flex', justifyContent: 'center' }}>
                    <Toggle
                      checked={getVal(localRolePermissions[role], section.accessKey, role)}
                      onChange={(v) => handlePermChange(role, section.accessKey, v)}
                    />
                  </div>
                ))}
              </div>

              {/* Расширенные права */}
              <div style={{
                display: 'grid',
                gridTemplateRows: isExpanded ? '1fr' : '0fr',
                transition: 'grid-template-rows 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}>
                <div style={{ overflow: 'hidden' }}>
                  <div style={{ padding: '0 20px 20px 70px' }}>
                    {section.note && (
                      <div style={{
                        fontSize: '12.5px',
                        color: 'var(--text-secondary)',
                        padding: '10px 14px',
                        background: 'rgba(99, 102, 241, 0.04)',
                        borderLeft: '3px solid var(--wb-primary)',
                        borderRadius: '0 8px 8px 0',
                        marginBottom: section.permissions.length > 0 ? '16px' : 0,
                        lineHeight: 1.5,
                      }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign:'middle',marginRight:'6px',flexShrink:0}}><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg> {section.note}
                      </div>
                    )}

                    {section.permissions.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div style={{
                          fontSize: '11px',
                          fontWeight: 700,
                          color: 'var(--text-secondary)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.8px',
                          marginBottom: '8px',
                        }}>
                          Детальные права
                        </div>

                        {section.permissions.map(perm => (
                          <div
                            key={perm.key}
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr repeat(3, 90px)',
                              gap: '8px',
                              alignItems: 'center',
                              padding: '10px 0',
                              borderBottom: '1px solid var(--surface-border)',
                            }}
                          >
                            <div>
                              <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--text-primary)' }}>
                                {perm.label}
                              </div>
                              <div style={{ fontSize: '11.5px', color: 'var(--text-secondary)', marginTop: '2.5px', lineHeight: 1.4 }}>
                                {perm.desc}
                              </div>
                            </div>
                            {ROLES.map(role => {
                              const sectionEnabled = getVal(localRolePermissions[role], section.accessKey, role)
                              return (
                                <div key={role} style={{ display: 'flex', justifyContent: 'center' }}>
                                  <Toggle
                                    checked={getVal(localRolePermissions[role], perm.key, role)}
                                    onChange={(v) => handlePermChange(role, perm.key, v)}
                                    disabled={!sectionEnabled}
                                  />
                                </div>
                              )
                            })}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
